import "css/common.css";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Cookies } from "react-cookie";
import axios from "axios";
import { openAlert } from "redux/slices/alertSlice";
import Alert from "components/common/alert";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import NotFound from "pages/common/notFound";
import Signin from "pages/account/signin";
import SigninMobile from "pages/mobile/account/signinMobile";
import App from "pages/app";
import AppMobile from "pages/mobile/appMobile";
import Menu from "pages/menu";
import "lang/i18n";
import { insertUserInfo } from "redux/slices/userSlice";
import { insertAllowPageInfo } from "redux/slices/allowPageSlice";
import Demo from "demo";
import { useMediaQuery } from "react-responsive";
import { insertSystemInfo } from "redux/slices/systemInfoSlice";
import QrScan from "pages/qr/qrScan";
import QrCodeTest from "pages/qr/qrCodeTest";

/**
 * 파일명: root.js
 * 작성자: 권도훈
 * 작성일자: 23.04.06
 * 설명: 메인 라우터
 * axios 설정 처리
 * 스크린 사이즈에 따른 css 처리
 */
function Root() {
  //반응형
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const isTablet = useMediaQuery({
    query: "(min-width: 768px) and (max-width: 1024px)",
  });

  //dispatch
  const dispatch = useDispatch();

  //cookie
  const cookies = new Cookies();

  //selector
  const userPayload = useSelector((state) => state.user);

  //env
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const authNm = process.env.REACT_APP_AUTH_TOKEN;
  // const authTime = process.env.REACT_APP_AUTH_TOKEN_TIME;
  const refreshNm = process.env.REACT_APP_REFRESH_TOKEN;
  const domain = process.env.REACT_APP_DOMAIN;

  const [init, setInit] = useState(true);

  const id = new URL(window.location.href).searchParams.get("id");
  const type = new URL(window.location.href).searchParams.get("type");
  const endpointId = new URL(window.location.href).searchParams.get(
    "endpointId"
  );

  //set screen size
  const setScreenSize = useCallback(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    let prevVisualViewport = 0;
    const currentVisualViewportHeight = window.visualViewport.height;

    if (
      prevVisualViewport - 30 > currentVisualViewportHeight &&
      prevVisualViewport - 100 < currentVisualViewportHeight
    ) {
      const scrollHeight = window.document.scrollingElement.scrollHeight;
      const scrollTop = scrollHeight - window.visualViewport.height;

      window.scrollTo(0, scrollTop);
    }

    prevVisualViewport = window.visualViewport.height;

    window.visualViewport.onresize = prevVisualViewport;
  }, []);

  //refresh user info
  const refreshInfo = useCallback(() => {
    axios
      .get("/user/refresh/info")
      .then((res) => {
        if (res.status === 200) {
          const memeberInfo = res.data.MEMBER_INFO;

          dispatch(
            insertUserInfo({
              name: memeberInfo.MEMBER_NM,
              id: memeberInfo.MEMBER_ID,
            })
          );

          const allowPages = res.data.ALLOW_PAGES;

          if (isMobile || isTablet) {
            dispatch(
              insertAllowPageInfo({
                pages: allowPages.filter((item) => item.MOBILE_YN === "Y"),
              })
            );
          } else {
            dispatch(insertAllowPageInfo({ pages: allowPages }));
          }
        }
      })
      .catch(() => {
        const effectCookies = new Cookies();
        effectCookies.remove(authNm, { path: "/", domain: domain });
        effectCookies.remove(refreshNm, { path: "/", domain: domain });
        if (id !== null) {
          window.location.href =
            type !== null
              ? process.env.REACT_APP_CLIENT_URL + "?id=" + id + "&type=" + type
              : process.env.REACT_APP_CLIENT_URL + "?id=" + id;
        } else if (endpointId !== null) {
          window.location.href =
            process.env.REACT_APP_CLIENT_URL + "?endpointId=" + endpointId;
        } else {
          window.location.href = process.env.REACT_APP_CLIENT_URL;
        }
      });
  }, [
    authNm,
    dispatch,
    domain,
    endpointId,
    id,
    isMobile,
    isTablet,
    refreshNm,
    type,
  ]);

  // 시스템 설정 정보 조회
  const getSystemInfo = useCallback(() => {
    axios
      .get("/schedule/systemInfo")
      .then((res) => {
        dispatch(
          insertSystemInfo({
            systemType: res.data.SYSTEM_TYPE,
          })
        );
      })
      .catch(() => {
        const effectCookies = new Cookies();
        effectCookies.remove(authNm, { path: "/", domain: domain });
        effectCookies.remove(refreshNm, { path: "/", domain: domain });

        if (id !== null) {
          window.location.href =
            type !== null
              ? process.env.REACT_APP_CLIENT_URL + "?id=" + id + "&type=" + type
              : process.env.REACT_APP_CLIENT_URL + "?id=" + id;
        } else if (endpointId !== null) {
          window.location.href =
            process.env.REACT_APP_CLIENT_URL + "?endpointId=" + endpointId;
        } else {
          window.location.href = process.env.REACT_APP_CLIENT_URL;
        }
      });
  }, [authNm, dispatch, domain, endpointId, id, refreshNm, type]);

  useEffect(() => {
    if (userPayload.id !== "" && init) {
      getSystemInfo();
      setInit(false);
    }
  }, [getSystemInfo, init, userPayload.id]);

  //resize eventListener
  useLayoutEffect(() => {
    setScreenSize();
    window.addEventListener("resize", () => setScreenSize());
  });

  useLayoutEffect(() => {
    const effectCookies = new Cookies();
    const refreshCookie = effectCookies.get(refreshNm);

    if (userPayload.name === "" && refreshCookie !== undefined) {
      refreshInfo();
    }
  }, [authNm, refreshInfo, refreshNm, userPayload.name]);

  //axios
  const inessentialList = [
    "/user/login",
    "/user/password",
    "/user/password/authcode",
    "/user/demo",
  ];
  axios.defaults.url = serverUrl;
  axios.defaults.baseURL = serverUrl;
  axios.defaults.withCredentials = false;

  //axios request
  axios.interceptors.request.use(
    async (config) => {
      if (config.params == null) {
        config.params = {};
      }

      if (!inessentialList.includes(config.url)) {
        const authCookie = cookies.get(authNm);
        const refreshCookie = cookies.get(refreshNm);

        if (authCookie === undefined && refreshCookie === undefined) {
          cookies.remove(authNm, { path: "/", domain: domain });
          cookies.remove(refreshNm, { path: "/", domain: domain });

          if (id !== null) {
            window.location.href =
              type !== null
                ? process.env.REACT_APP_CLIENT_URL +
                  "?id=" +
                  id +
                  "&type=" +
                  type
                : process.env.REACT_APP_CLIENT_URL + "?id=" + id;
          } else if (endpointId !== null) {
            window.location.href =
              process.env.REACT_APP_CLIENT_URL + "?endpointId=" + endpointId;
          } else {
            window.location.href = process.env.REACT_APP_CLIENT_URL;
          }
        }

        config.headers.Authorization = `Bearer ${authCookie}`;
        config.headers.RefreshAuthorization = `Bearer ${refreshCookie}`;
      }

      return config;
    },
    (err) => {
      return Promise.reject(err);
    }
  );

  //axios response
  axios.interceptors.response.use(
    (res) => {
      if (res.headers.refresh_authorization !== undefined) {
        cookies.remove(authNm, { path: "/", domain: domain });
        cookies.set(authNm, res.headers.refresh_authorization, {
          path: "/",
          // maxAge: authTime,
          domain: domain,
        });
      }

      return res;
    },
    (err) => {
      const response = err.response;

      if (response !== undefined) {
        const status = response.status;

        if (status === 400) {
          cookies.remove(authNm, { path: "/", domain: domain });
          cookies.remove(refreshNm, { path: "/", domain: domain });
        } else if (status === 401) {
          cookies.remove(authNm, { path: "/", domain: domain });
          cookies.remove(refreshNm, { path: "/", domain: domain });
          dispatch(
            openAlert({
              title: "알림",
              content: err.response.data,
              submitEventHandler: () => {
                if (id !== null) {
                  window.location.href =
                    type !== null
                      ? process.env.REACT_APP_CLIENT_URL +
                        "?id=" +
                        id +
                        "&type=" +
                        type
                      : process.env.REACT_APP_CLIENT_URL + "?id=" + id;
                } else if (endpointId !== null) {
                  window.location.href =
                    process.env.REACT_APP_CLIENT_URL +
                    "?endpointId=" +
                    endpointId;
                } else {
                  window.location.href = process.env.REACT_APP_CLIENT_URL;
                }
              },
            })
          );
        } else if (status === 404) {
          throw new Error("알 수 없는 요청입니다.");
        }
      } else {
        dispatch(
          openAlert({
            title: "오류",
            content: err.code,
            submitEventHandler: () => {},
          })
        );
      }

      return Promise.reject(err);
    }
  );

  return (
    <div className="container">
      <Alert />
      <BrowserRouter>
        {isMobile || isTablet ? (
          <Routes>
            <Route
              path={process.env.REACT_APP_CLIENT_URL}
              element={<SigninMobile />}
            />
            <Route
              path={process.env.REACT_APP_CLIENT_URL + "app/*"}
              element={<AppMobile />}
            />

            <Route
              path={process.env.REACT_APP_CLIENT_URL + "qr"}
              element={<QrScan />}
            />
            <Route
              path={process.env.REACT_APP_CLIENT_URL + "qrCode"}
              element={<QrCodeTest />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : (
          <Routes>
            <Route
              path={process.env.REACT_APP_CLIENT_URL}
              element={<Signin />}
            />
            <Route
              path={process.env.REACT_APP_CLIENT_URL + "demo"}
              element={<Demo />}
            />
            <Route
              path={process.env.REACT_APP_CLIENT_URL + "menu"}
              element={<Menu />}
            />
            <Route
              path={process.env.REACT_APP_CLIENT_URL + "app/*"}
              element={<App />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </BrowserRouter>
    </div>
  );
}

export default Root;
